import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "@localization/useTranslation";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { EntityType } from "@common/Common.types";
import { getSelectableBusinessAreasUrl, useSelectableTagsQuery } from "@common/hooks/useSelectableItemQueries";
import { useRiskAreasContext } from "@common/providers/riskAreasProvider/RiskAreasProvider";
import { useUrlProvider } from "../../useUrlProvider";
import { getLink } from "../../taskManagement/taskPane/taskRelationsBox/TaskRelationsBox.hooks";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery";
import { SelectableColoredItem } from "../../processingActivity/ProcessingActivity.types";
import { entityTypeLabel, RiskChip } from "../riskOperationalOverview/RiskOperationalOverview.types";
import { RiskAreaTab } from "../riskArea/RiskArea.types";
import { RiskExposureMode } from "../riskExposure/RiskExposure.types";
import { formatExposureValue } from "../riskExposure/formatExposureValue";
import { RiskAssessmentOverviewResult } from "./RiskAssessmentsOverview.types";
import { getRiskAssessmentSubjectUrl, isLevelOverAppetite, isMitigatable } from "./RiskAssessmentsOverview.utils";

// The entity page around the scenario table: breadcrumb, view switcher and the top-bar tiles. Reads the same
// overview endpoint as ScenarioAssessmentsTable through the same query key, so the two share one request and the
// tiles refresh whenever the table saves something.
export function useRiskAssessmentsOverview(riskAreaId: string | undefined, entityType: EntityType, entityId: string | undefined) {
    const { translateString, translateNumber } = useTranslation();
    const navigate = useNavigate();
    const urlProvider = useUrlProvider();
    const { riskAreas } = useRiskAreasContext();

    const { data, isLoading } = useDotLegalQuery<RiskAssessmentOverviewResult>(
        getRiskAssessmentSubjectUrl(riskAreaId ?? "", { kind: "entity", entityType, entityId: entityId ?? "" })
    );

    const responsibleQuery = useResponsibleQuery();
    const businessAreaQuery = useDotLegalQuery<Array<SelectableColoredItem>>(getSelectableBusinessAreasUrl());
    const tagQuery = useSelectableTagsQuery();

    const [isEntityGraphOpen, setIsEntityGraphOpen] = useState(false);

    const riskAreaName = riskAreas.find((x) => x.id === riskAreaId)?.name;
    const entityName = data?.name ?? "";

    // Memoised because the table's bulk selection lives on this page: without it every row checkbox re-renders
    // the page and rebuilds both lookups from the customer's whole tag and business area lists.
    const tagChipById = useMemo(() => new Map<string, RiskChip>((tagQuery.data ?? []).map((x) => [x.id, { name: x.name, color: x.color }])), [tagQuery.data]);
    const businessAreaChipById = useMemo(
        () => new Map<string, RiskChip>((businessAreaQuery.data ?? []).map((x) => [x.id, { name: x.name, color: x.color }])),
        [businessAreaQuery.data]
    );

    const toChips = (ids: Array<string>, source: Map<string, RiskChip>) => ids.map((id) => source.get(id)).filter((chip): chip is RiskChip => !!chip);

    const tagChips = toChips(data?.tagIds ?? [], tagChipById);
    const businessAreaChips = toChips(data?.businessAreaIds ?? [], businessAreaChipById);
    // One id to resolve, so a scan beats indexing the customer's entire user list.
    const responsibleName = (data?.responsibleId ? responsibleQuery.userData?.find((x) => x.id === data.responsibleId)?.name : undefined) ?? "";

    const entityLink = entityId ? getLink(entityType, entityId, undefined, urlProvider) : "";

    const getTypeLabel = (type: EntityType) => entityTypeLabel(type, translateString);

    const isOverAppetite = (level: number) => isLevelOverAppetite(level, data?.appetite ?? 0);

    // Whether the Bulk mitigate button has anything to act on. Derived from every scenario rather than the table's
    // filtered rows, which live in the table's own hook.
    const hasMitigatableScenarios = (data?.scenarios ?? []).some(isMitigatable);

    // This page is the drill-down from the area's Operational view, so that tab stays selected; picking a
    // different view navigates back up to the area with the chosen view applied.
    const onSelectView = (tab: RiskAreaTab) => {
        if (riskAreaId) navigate(urlProvider.getRiskAreaUrl(riskAreaId, tab));
    };

    // The header exposure tiles, summed (Financial) or averaged (Scale) over the entity's scenarios: current
    // exposure, estimated mitigation cost (always money), and exposure after mitigation. All three are shown
    // together only when exposure is measured and something carries a value; null hides the whole row.
    const exposureTiles = (() => {
        const exposure = data?.exposure;
        if (!data || !exposure || exposure.withExposureCount === 0) return null;
        const financial = data.exposureMode === RiskExposureMode.Financial;
        // Never null here: the mode is not None and a missing total reads as zero rather than as "nothing captured".
        const formatModeValue = (value: number | null) =>
            formatExposureValue(value ?? 0, data.exposureMode, data.exposureCurrencyCode, data.exposureScaleUpperBound, translateNumber)!;
        return {
            gross: formatModeValue(financial ? exposure.grossSum : exposure.grossAverage),
            cost: exposure.mitigationCostSum ? `${translateNumber(exposure.mitigationCostSum)} ${data.exposureCurrencyCode}` : undefined,
            afterMitigation: formatModeValue(financial ? exposure.afterMitigationSum : exposure.afterMitigationAverage),
        };
    })();

    return {
        isLoading,
        riskAreaName,
        entityName,
        description: data?.description ?? undefined,
        entityLink,
        getTypeLabel,
        tagChips,
        businessAreaChips,
        responsibleName,
        highestRisk: data?.highestRisk ?? null,
        highestRiskIndicators: data?.riskLevelIndicators ?? [],
        linkedRisk: data?.linkedRisk ?? null,
        isOverAppetite,
        exposureTiles,
        hasMitigatableScenarios,
        selectedView: RiskAreaTab.Operational,
        onSelectView,
        isEntityGraphOpen,
        openEntityGraph: () => setIsEntityGraphOpen(true),
        closeEntityGraph: () => setIsEntityGraphOpen(false),
    };
}
